import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type CheckboxVariant = "default" | "card";

interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: string;
  description?: string;
  variant?: CheckboxVariant;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      variant = "default",
      checked = false,
      disabled = false,
      onCheckedChange,
      id: idProp,
      className,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;

    function handleChange() {
      if (!disabled && onCheckedChange) {
        onCheckedChange(!checked);
      }
    }

    return (
      <label
        htmlFor={id}
        className={cn(
          variant === "card"
            ? cn(
                "flex items-start gap-3 rounded-lg border p-4 transition-colors duration-ev",
                checked
                  ? "border-ev-green bg-ev-success-bg"
                  : "border-ev-border bg-ev-surface hover:bg-ev-surface-subtle",
                disabled && "opacity-50 cursor-not-allowed",
                !disabled && "cursor-pointer",
              )
            : cn(
                "inline-flex items-start gap-2",
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
              ),
          className,
        )}
      >
        {/* Native checkbox -- visually hidden but accessible */}
        <input
          type="checkbox"
          id={id}
          ref={ref}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          className="peer sr-only"
          {...props}
        />

        {/* Visual box -- decorative, hidden from assistive tech */}
        <span
          aria-hidden="true"
          className={cn(
            "relative flex h-4 w-4 shrink-0 items-center justify-center rounded transition-colors duration-ev",
            checked
              ? "border-ev-blue bg-ev-blue"
              : "border border-ev-border-strong bg-ev-surface",
            disabled && "opacity-50 cursor-not-allowed",
            "peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ev-focus",
          )}
        >
          {checked && (
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          )}
        </span>

        {/* Label and optional description */}
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-ev-text-primary leading-4">
            {label}
          </span>
          {description && (
            <span className="text-sm font-medium text-ev-text-secondary">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

interface CheckboxGroupProps {
  label?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

function CheckboxGroup({
  label,
  description,
  children,
  className,
}: CheckboxGroupProps) {
  return (
    <fieldset className={cn("flex flex-col gap-3", className)} role="group" aria-label={label}>
      {label && (
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-ev-text-primary">
            {label}
          </span>
          {description && (
            <span className="text-sm font-medium text-ev-text-secondary">
              {description}
            </span>
          )}
        </div>
      )}
      {children}
    </fieldset>
  );
}

export { Checkbox, CheckboxGroup };
